import { Box, Flex, FormControl, FormErrorMessage, Text } from '@chakra-ui/react'
import type { FieldError } from 'BrokerTsLib/src/rhf'
import { FormProvider, useFormContext } from 'BrokerTsLib/src/rhf'

import { useApp } from '@/hooks/useApp'
import type { InsuranceParamsPropertyDto } from 'BrokerTsLib/src/dtos'
import { Product, RiskVariant, Selection } from 'BrokerTsLib/src/enums'
import { useEffect, useState } from 'react'
import { nameof } from 'ts-simple-nameof'
import { RadioIcon } from '../../components/RadioIcon'
import { RadioIconGroup } from '../../components/RadioIconGroup'
import { RadioStack } from '../../components/RadioStack'
import { Check, Close } from '../../icons'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import type {
  IPropertyObject
} from '../../utils/propertyInsuranceTypes'
import {
  flatObjects,
  houseObjects
} from '../../utils/propertyInsuranceTypes'

const ObjectIcon = ({
  isDisabled = false,
  colorScheme
}: {
  isDisabled: boolean
  colorScheme: string | undefined
}) => {
  return (
    <Box
      sx={{
        display: 'grid',
        placeContent: 'center',
        w: 6,
        h: 6,
        bg: isDisabled ? 'gray.50' : `${colorScheme ?? 'green'}.50`,
        borderRadius: 'full',
      }}
    >
      {!isDisabled && <Check width="1rem" height="1rem" color={`${colorScheme ?? 'green'}.500`} />}
      {isDisabled && <Close width="1rem" height="1rem" color="gray.500" />}
    </Box>
  )
}


const Object = ({
  object,
  isDisabled = false,
}: {
  object: IPropertyObject
  insuranceType?: number
  isDisabled: boolean
}) => {
  let colorScheme

  switch (object.insuranceType) {
    case RiskVariant.Realty:
      colorScheme = 'primary'
      break
    case RiskVariant.Household:
      colorScheme = 'secondary'
      break
  }

  return (
    <Flex sx={{ alignItems: 'center', gap: 2 }}>
      {object.name &&
        <>
          <ObjectIcon isDisabled={isDisabled} colorScheme={colorScheme} />

          <Text
            sx={{
              color: isDisabled ? 'gray.500' : `${colorScheme}.500`,
              fontSize: 'sm',
              fontWeight: 'medium',
            }}
          >
            {object.name}
          </Text>
        </>
      }
    </Flex>
  )
}

const isObjectDisabled = (
  object: IPropertyObject,
  insuranceType?: number,
) => {
  if (!insuranceType) return true

  if (insuranceType === RiskVariant.HouseholdAndRealty) return false

  return insuranceType !== object.insuranceType
}

const ObjectsList = ({
  objects,
  insuranceType,
}: {
  objects: Array<IPropertyObject>
  insuranceType?: number
}) => {
  return (
    <Box
      sx={{
        display: 'grid',
        w: '100%',
        gridAutoFlow: { base: 'row', md: 'column' },
        gridTemplateRows: { base: 'auto', md: 'repeat(5, 1fr)' },
        gridTemplateColumns: { base: '1fr 1fr', md: 'repeat(auto-fill, minmax(22%, 1fr))' },
        columnGap: 4,
        rowGap: 4,
      }}
    >
      {objects &&
        objects.length > 0 &&
        objects.map((object) => {
          return (
            object && (
              <Object
                key={`${object.name}-${object.id}`}
                object={object}
                insuranceType={insuranceType}
                isDisabled={isObjectDisabled(object, insuranceType)}
              />
            )
          )
        })}
    </Box>
  )
}

const getPropertyTypesObject = (type: number) => {
  switch (type) {
    default:
    case Selection.RodinnyDum:
      return houseObjects
    case Selection.Byt:
      return flatObjects
  }
}


export type RealtyTypeSelectorProps = {
  isDisabled?: boolean
  prefix: string
}

export const RealtyTypeSelector = ({
  isDisabled = false,
  prefix
}: RealtyTypeSelectorProps) => {
  const { control, register } = useFormContext()

  return (
    <RadioIconGroup
      control={control}
      name={register(prefix).name}
      isDisabled={isDisabled}
    >
      <RadioStack >
        <RadioIcon icon="house" value={Selection.RodinnyDum.toString()}>
          Rodinný dům
        </RadioIcon>
        <RadioIcon icon="household" value={Selection.Byt.toString()}>
          Bytová jednotka
        </RadioIcon>
        <RadioIcon icon="hut" value={Selection.RekreacniObjekt.toString()}>
          Rekreační objekt
        </RadioIcon>
      </RadioStack>
    </RadioIconGroup>
  )
}

export const PropertyTypeSelector = ({
  isDisabled,
  prefix
}: RealtyTypeSelectorProps) => {
  const { watch, setValue } = useFormContext()
  const { product, setProduct } = useApp()
  const { insuranceParams, insuranceParamsId: paramsId, refetchInsuranceParams, error, isLoadingData } = useInsuranceParamsContext()

  const variant = watch(prefix)

  const handleSelectProduct = (product: Product) => {
    setProduct(product)

    if (insuranceParams)
      insuranceParams.generalParams.insuranceCategoryType = product
  }

  return (
    <RadioStack>
      <RadioIcon icon="householdProperty"
        isChecked={variant === RiskVariant.HouseholdAndRealty && product == Product.PropertyInsurance}
        onChange={() => {
          handleSelectProduct(Product.PropertyInsurance)
          setValue(prefix, RiskVariant.HouseholdAndRealty)
        }}
        isDisabled={isDisabled}
      >
        Nemovitost + Domácnost
      </RadioIcon>

      <RadioIcon icon="house" colorScheme="primary"
        isChecked={variant === RiskVariant.Realty && product == Product.PropertyInsurance}
        onChange={() => {
          handleSelectProduct(Product.PropertyInsurance)
          setValue(prefix, RiskVariant.Realty)
        }}
        isDisabled={isDisabled}
      >
        Nemovitost
      </RadioIcon>

      <RadioIcon icon="household" colorScheme="secondary"
        isChecked={variant === RiskVariant.Household && product == Product.PropertyInsurance}
        onChange={() => {
          handleSelectProduct(Product.PropertyInsurance)
          setValue(prefix, RiskVariant.Household)
        }}
        isDisabled={isDisabled}
      >
        Domácnost
      </RadioIcon>

      <RadioIcon icon="responsibility" colorScheme="secondary"
        isChecked={product === Product.LiabilityInsurance}
        onChange={() => {
          handleSelectProduct(Product.LiabilityInsurance)
          setValue(prefix, null)
        }}
        isDisabled={isDisabled}
      >
        Samostatná odpovědnost
      </RadioIcon>
    </RadioStack>
  )
}

export const PropertyInsuranceTypeSelector = () => {
  const { insuranceParams } = useInsuranceParamsContext()
  const { product } = useApp()
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const methods = useFormContext<InsuranceParamsPropertyDto>()
  const { watch, formState: { errors } } = methods
  const [objects, setObjects] = useState<IPropertyObject[]>([])
  const propertyTypeValue = watch('property.data.realtyTypeId')
  const insuranceTypeValue = watch('property.type')

  useEffect(() => {
    const propertyTypeObject: IPropertyObject[] = propertyTypeValue ? getPropertyTypesObject(parseInt(propertyTypeValue.toString())) : []
    setObjects(propertyTypeObject)
  }, [propertyTypeValue])

  return (
    <FormProvider {...methods}>
      <Box sx={{ w: '100%', display: 'grid', gap: 8 }}>
        <FormControl isInvalid={!!errors.property?.type}>
          <PropertyTypeSelector
            isDisabled={isContracted}
            prefix={nameof<InsuranceParamsPropertyDto>(x => x.property.type)}
          />
          {errors.property?.type && <FormErrorMessage>{(errors.property?.type as FieldError)?.message}</FormErrorMessage>}
        </FormControl>

        {insuranceTypeValue && product === Product.PropertyInsurance &&
          <>
            <FormControl isInvalid={!!errors.property?.data?.realtyTypeId}>
              <RealtyTypeSelector
                isDisabled={isContracted}
                prefix={nameof<InsuranceParamsPropertyDto>(x => x.property.data.realtyTypeId)}
              />
              {errors.property?.data?.realtyTypeId && <FormErrorMessage>{errors.property?.data?.realtyTypeId?.message}</FormErrorMessage>}
            </FormControl>

            {objects && objects.length > 0 && <ObjectsList objects={objects} insuranceType={insuranceTypeValue ? parseInt(insuranceTypeValue.toString()) : undefined} />}
          </>
        }
      </Box>
    </FormProvider>
  )
}
